import { useCallback, useMemo } from 'react';
import type { NavigationContext, StepId, UseNavigationReturn } from './types';
import { resolveNavigation, resolveNavigationWithOverride } from './utils';
import { useWizard } from '@/context/wizard';
import type { RefundRequestWizardProps } from '@/packages/subsidy/pages/refund-request/steps/resource-description/interfaces';
import { RefundRequestWizardStepIds } from '@/packages/subsidy/pages/refund-request/steps/resource-description/steps/step-ids';

const navigationDelay = 500;

const buildNavigationContext = (
  values: Partial<RefundRequestWizardProps>
): NavigationContext => {
  const policies = values?.policies ?? [];
  const hasOnePolicy = policies.length === 1;
  const selectedPolicy =
    values?.[RefundRequestWizardStepIds.SelectPolicy] ?? policies[0];
  const eligibleResourceType =
    selectedPolicy?.configuration?.eligible_resource_type;
  const resourceApprovals = values?.resourceApprovals ?? [];
  const hasAvailableResourceApprovals = resourceApprovals.length >= 1;

  return {
    values,
    hasOnePolicy,
    hasAvailableResourceApprovals,
    eligibleResourceType,
  };
};

interface UseRefundRequestNavigationOptions {
  stepId: StepId;
}

export const useRefundRequestNavigation = ({
  stepId,
}: UseRefundRequestNavigationOptions): UseNavigationReturn => {
  const { actions, values } = useWizard<RefundRequestWizardProps>();

  const context = useMemo(() => buildNavigationContext(values), [values]);

  const { next, previous } = useMemo(
    () => resolveNavigation(stepId, context),
    [stepId, context]
  );

  const navigate = useCallback(
    (targetStep: StepId | null, delay = false) => {
      if (!targetStep) return;

      const doNavigate = () => actions.navigate(targetStep);

      if (delay) {
        setTimeout(doNavigate, navigationDelay);
      } else {
        doNavigate();
      }
    },
    [actions]
  );

  const handleNext = useCallback(
    (newValues?: Partial<RefundRequestWizardProps>) => {
      if (newValues) {
        actions.updateValues(newValues);
      }

      const targetStep = newValues
        ? resolveNavigationWithOverride(stepId, context, newValues).next
        : next;

      navigate(targetStep, true);
    },
    [actions, context, navigate, next, stepId]
  );

  const handleBack = useCallback(() => {
    navigate(previous);
  }, [navigate, previous]);

  return {
    handleNext,
    handleBack,
    values,
    canGoNext: next !== null,
    canGoBack: previous !== null,
  };
};

export const useNavigationContext = (): NavigationContext => {
  const { values } = useWizard<RefundRequestWizardProps>();
  return useMemo(() => buildNavigationContext(values), [values]);
};
